import { nexusRouteFlags } from "@/lib/nexusRouteFlags.ts"
import { removeLeadingSlash } from "@/lib/utils.ts"
import {
  ensureStartsWithMyAccountPath,
  getHelpWebsiteUrl,
  getPublicWebsiteUrl,
} from "@/lib/websiteUrls.ts"
import shouldServeNexus from "@/middleware/shouldServeNexus.ts"
import {
  isExternalRoute,
  isInternalRoute,
} from "@/plugins/router/routesHelper.ts"
import { LinkOpenerService } from "@/services/shared/LinkOpenerService"
import { computed, MaybeRef, unref } from "vue"
import { Router } from "vue-router"

export default function useLinkResolver(
  initialTo: MaybeRef<string | undefined>,
  injectedRouter: Router,
) {
  const allRoutes = injectedRouter.getRoutes()
  const to = computed(() => unref(initialTo))

  const isLiveRoute = computed(() => {
    if (to.value === undefined) return false

    // the legacy site doesn't have a trailing slash on the home route,
    // we just need to deal with that :(
    const fullPath =
      to.value === "/" ? "/myaccount" : ensureStartsWithMyAccountPath(to.value)

    const routeForNexusCheck = fullPath.split(/[?#]/)[0]

    return shouldServeNexus(routeForNexusCheck, nexusRouteFlags)
  })

  // anything in the internal routes that is not explicitly disabled by the
  // shouldServeNexus function is rendered using a router-link
  const isInternalAndLive = computed(
    () => isInternalRoute(to.value, allRoutes) && isLiveRoute.value,
  )

  // legacy website URLS are opened in the in-app browser but also nexus routes
  // that are not yet live, or are feature flagged
  const isLegacyAppLink = computed(() => {
    const isInternalButNotLive =
      isInternalRoute(to.value, allRoutes) && !isLiveRoute.value

    const isOnOurDomain = !!(
      to.value?.startsWith("/myaccount/") ||
      to.value?.startsWith(getPublicWebsiteUrl())
    )

    return (
      to.value?.startsWith(getHelpWebsiteUrl()) ||
      (isExternalRoute(
        `${getPublicWebsiteUrl()}${removeLeadingSlash(to.value)}`,
      ) &&
        !isInternalAndLive.value) ||
      (isExternalRoute(to.value) && !isInternalAndLive.value) ||
      isInternalButNotLive ||
      (isOnOurDomain && !isInternalAndLive.value)
    )
  })

  const isAnchorElement = computed(() => !isInternalAndLive.value)

  const correctedUrl = computed(() => {
    // an internal link which is not live should use the legacy my account url,
    // but will have been passed to us as Route.tariff for example and so will
    // not include the hostname or myaccount prefixes, we should add them
    if (isInternalRoute(to.value, allRoutes) && !isLiveRoute.value) {
      return ensureStartsWithMyAccountPath(to.value)
    }

    return isAnchorElement.value || !to.value
      ? to.value
      : ensureStartsWithMyAccountPath(to.value)
  })

  const linkAttributes = computed(() =>
    isAnchorElement.value ? { href: correctedUrl.value } : {},
  )

  const navigateToUrl = async (
    event?: MouseEvent,
  ): Promise<{ didOpenBrowser: boolean }> => {
    if (!correctedUrl.value) return { didOpenBrowser: false }

    if (isInternalAndLive.value) {
      await injectedRouter.push(correctedUrl.value)

      return { didOpenBrowser: false }
    }

    return LinkOpenerService.openUrl(
      to.value ?? correctedUrl.value,
      correctedUrl.value,
      isLegacyAppLink.value,
      event,
    )
  }

  return {
    isAnchorElement,
    isLegacyAppLink,
    linkAttributes,
    correctedUrl,
    navigateToUrl,
  }
}
